import "./careerPhone.css";
import { KITS } from "@golazo/shared";
import { t } from "../i18n/index.js";
import { canAnswer, careerRelations, nextFixture, tableOf } from "../career/career.js";
import { CLUBS_PER_DIVISION, MATCHDAYS, type Career, type Club, type PhoneMessage, type Relations, type Sender } from "../career/model.js";
import { AGENT_NAME, PAPER_NAME } from "../career/names.js";
import { STYLE_BLURB, STYLE_LABEL } from "../career/phone.js";
import { objectiveKey, questionOf, squadOf } from "../career/phoneTalk.js";
import { clubById } from "../career/world.js";
import { escapeHtml, kitTokenHtml, ordinal, phoneText } from "./careerText.js";

/**
 * El movil de la carrera, como el de GTA: conversaciones por contacto, que se
 * contestan, con adjuntos que sirven para jugar (el informe del rival, el
 * encargo del mister) y con la gente reaccionando a lo que hiciste.
 *
 * Solo pinta (HTML) y dice que hay pendiente. Lo que pasa al tocar lo hace
 * `careerHub.ts` con los `data-act` de aqui: `thread`, `inbox`, `reply`.
 */
export type ThreadId = Sender;

export interface PhoneUi {
  /** Conversacion abierta, o null para la bandeja. */
  thread: ThreadId | null;
  /** Id de un mensaje que aun se esta "escribiendo" (se pinta el "…"). */
  typing: number | null;
  /** Lo que acaba de cambiar al contestar ("Míster ▲4"), para enseñarlo una vez. */
  effect: Partial<Relations> | null;
}

/** Hasta donde has leido cada conversacion (id del ultimo mensaje visto). */
export type SeenMap = Partial<Record<ThreadId, number>>;

const THREAD_ORDER: readonly ThreadId[] = ["coach", "squad", "agent", "mom", "rival", "press", "fans", "system"];

const AVATAR: Record<ThreadId, { icon: string; tone: string }> = {
  coach: { icon: "📋", tone: "coach" },
  squad: { icon: "👥", tone: "squad" },
  agent: { icon: "💼", tone: "agent" },
  mom: { icon: "❤️", tone: "mom" },
  rival: { icon: "😈", tone: "rival" },
  press: { icon: "📰", tone: "press" },
  fans: { icon: "📣", tone: "fans" },
  system: { icon: "🎽", tone: "system" },
};

function threadTitle(c: Career, id: ThreadId): { name: string; sub: string } {
  const club = clubById(c.clubs, c.season.club);
  switch (id) {
    case "coach":
      return { name: t("Míster {coach}", { coach: club.coach }), sub: t("Tu entrenador · {club}", { club: club.name }) };
    case "squad":
      return {
        name: t("Vestuario · {club}", { club: club.name }),
        sub: squadOf(c.seed, club.id)
          .map((m) => m.name.split(" ")[0])
          .join(", "),
      };
    case "agent":
      return { name: AGENT_NAME, sub: t("Tu agente. Cobra el 10 %") };
    case "mom":
      return { name: t("Mamá"), sub: t("En línea. Siempre.") };
    case "rival":
      return { name: t("Rivales"), sub: t("Los que te quieren ganar") };
    case "press":
      return { name: PAPER_NAME, sub: t("Noticias. Acierta alguna") };
    case "fans":
      return { name: t("La Grada"), sub: t("Lo que dice la afición") };
    case "system":
      return { name: "FYNTA", sub: t("Equipaciones y premios") };
  }
}

export function threadsOf(c: Career): Array<{ id: ThreadId; messages: PhoneMessage[] }> {
  const by = new Map<ThreadId, PhoneMessage[]>();
  for (const m of c.feed) {
    const list = by.get(m.from) ?? [];
    list.push(m);
    by.set(m.from, list);
  }
  return [...by.entries()]
    .map(([id, messages]) => ({ id, messages }))
    .sort((a, b) => b.messages.at(-1)!.id - a.messages.at(-1)!.id || THREAD_ORDER.indexOf(a.id) - THREAD_ORDER.indexOf(b.id));
}

const unreadIn = (messages: readonly PhoneMessage[], seen: number): number =>
  messages.filter((m) => m.id > seen && !m.mine).length;

/** Lo que queda sin leer en todo el movil (para el globo de la pestaña). */
export function phoneUnread(c: Career, seen: SeenMap): number {
  return threadsOf(c).reduce((n, th) => n + unreadIn(th.messages, seen[th.id] ?? 0), 0);
}

/** Preguntas que aun se pueden contestar. */
export function pendingAnswers(c: Career): number {
  return c.feed.filter((m) => canAnswer(c, m)).length;
}

function whenText(m: Pick<PhoneMessage, "season" | "matchday">): string {
  return m.matchday >= MATCHDAYS ? t("T{s} · Fin de temporada", { s: m.season }) : t("T{s} · J{md}", { s: m.season, md: m.matchday + 1 });
}

// ------------------------------------------------------------------ relaciones

const RELATION_INFO: Record<keyof Relations, { label: string; hint: string }> = {
  coach: { label: "Míster", hint: "Cuanto te trabaja: con él de tu lado creces más rápido." },
  squad: { label: "Vestuario", hint: "Tus compañeros juegan mejor contigo cuanto mejor os lleváis." },
  fans: { label: "Afición", hint: "La fama trae mejores ofertas al acabar la temporada." },
};

function mood(v: number): string {
  if (v >= 80) return t("Te adora");
  if (v >= 60) return t("Bien");
  if (v >= 40) return t("Normal");
  if (v >= 20) return t("Tenso");
  return t("Fatal");
}

function relationsHtml(r: Relations): string {
  return `
    <div class="ph-rel" role="list" aria-label="${escapeHtml(t("Cómo te llevas"))}">
      ${(Object.keys(RELATION_INFO) as Array<keyof Relations>)
        .map((k) => {
          const v = r[k];
          const info = RELATION_INFO[k];
          return `
            <div class="ph-rel-item is-${k}" role="listitem" title="${escapeHtml(`${t(info.hint)} (${v}/100)`)}">
              <span class="ph-rel-label">${escapeHtml(t(info.label))}<em>${escapeHtml(mood(v))}</em></span>
              <span class="ph-rel-bar"><i style="width:${v}%"></i></span>
            </div>`;
        })
        .join("")}
    </div>`;
}

/** "Míster ▲4 · Afición ▼3", tras contestar. */
function effectHtml(effect: Partial<Relations>): string {
  const parts = (Object.keys(RELATION_INFO) as Array<keyof Relations>)
    .filter((k) => (effect[k] ?? 0) !== 0)
    .map((k) => {
      const n = effect[k]!;
      return `<span class="${n > 0 ? "is-up" : "is-down"}">${escapeHtml(t(RELATION_INFO[k].label))} ${n > 0 ? "▲" : "▼"}${Math.abs(n)}</span>`;
    });
  if (parts.length === 0) return "";
  return `<li class="ph-effect" role="status">${parts.join(" · ")}</li>`;
}

// ------------------------------------------------------------------ adjuntos

/** Forma del club esta temporada: sus ultimos resultados (V, E, D). */
function formOf(c: Career, club: Club): Array<"w" | "d" | "l"> {
  const tier = c.season.members.findIndex((m) => m.includes(club.id));
  if (tier < 0) return [];
  const members = c.season.members[tier]!;
  const me = members.indexOf(club.id);
  const out: Array<"w" | "d" | "l"> = [];
  for (const day of c.season.results[tier] ?? []) {
    const game = (day ?? []).find(([h, a]) => h === me || a === me);
    if (!game) continue;
    const [h, , hg, ag] = game;
    const mine = h === me ? hg : ag;
    const theirs = h === me ? ag : hg;
    out.push(mine > theirs ? "w" : mine < theirs ? "l" : "d");
  }
  return out.slice(-3);
}

function scoutCardHtml(c: Career, m: PhoneMessage): string {
  const rivalId = typeof m.params?.rivalId === "string" ? m.params.rivalId : null;
  const rival = rivalId ? c.clubs.find((x) => x.id === rivalId) : undefined;
  if (!rival) return "";
  const current = m.season === c.season.number;
  const tier = c.season.members.findIndex((members) => members.includes(rival.id));
  const stats = current && tier >= 0 ? tableOf(c, tier).find((r) => r.club === rival.id) : undefined;
  // Antes de la primera jornada todos tienen 0 puntos: el puesto no dice nada.
  const row = stats && stats.played > 0 ? tableOf(c, tier).findIndex((r) => r.club === rival.id) : -1;
  const form = current ? formOf(c, rival) : [];
  const formHtml = form.length
    ? form
        .map((f) => {
          // Simbolos y no letras: "V" ya es "Visitante" en el diccionario.
          const label = f === "w" ? t("Victoria") : f === "d" ? t("Empate") : t("Derrota");
          return `<i class="ph-form is-${f}" title="${escapeHtml(label)}" aria-label="${escapeHtml(label)}">${f === "w" ? "✓" : f === "d" ? "=" : "✗"}</i>`;
        })
        .join("")
    : `<span class="ph-muted">${escapeHtml(t("Sin partidos aún"))}</span>`;
  return `
    <div class="ph-card ph-scout">
      <header>${kitTokenHtml(KITS[rival.kitId], rival.name.charAt(0), "s")}<strong>${escapeHtml(t("Informe: {rival}", { rival: rival.name }))}</strong></header>
      <dl>
        ${row >= 0 ? `<div><dt>${escapeHtml(t("Puesto"))}</dt><dd>${escapeHtml(ordinal(row + 1))} / ${CLUBS_PER_DIVISION}</dd></div>` : ""}
        <div><dt>${escapeHtml(t("Forma"))}</dt><dd>${formHtml}</dd></div>
        ${stats && stats.played > 0 ? `<div><dt>${escapeHtml(t("Goles"))}</dt><dd>${stats.goalsFor} ${escapeHtml(t("a favor"))} · ${stats.goalsAgainst} ${escapeHtml(t("en contra"))}</dd></div>` : ""}
        <div><dt>${escapeHtml(t("Juegan"))}</dt><dd><b>${escapeHtml(t(STYLE_LABEL[rival.style]))}.</b> ${escapeHtml(t(STYLE_BLURB[rival.style]))}</dd></div>
        <div><dt>${escapeHtml(t("Ojo con"))}</dt><dd>${escapeHtml(rival.star)}</dd></div>
      </dl>
    </div>`;
}

function objectiveCardHtml(c: Career, m: PhoneMessage): string {
  const key = typeof m.params?.objective === "string" ? m.params.objective.slice(1) : "";
  if (!key) return "";
  const n = Number(m.params?.n ?? 0);
  const st = m.params?.st;
  let state: "pending" | "done" | "failed" | "past" = "past";
  if (st === 1) state = "done";
  else if (st === 0) state = "failed";
  else if (c.objective && c.objective.season === m.season && c.objective.matchday === m.matchday && !c.objective.result) state = "pending";
  const badge =
    state === "done" ? `✓ ${t("Cumplido")}` : state === "failed" ? `✗ ${t("Sin cumplir")}` : state === "pending" ? t("Para el próximo partido") : "";
  return `
    <div class="ph-card ph-objective is-${state}">
      <span class="ph-objective-icon" aria-hidden="true">🎯</span>
      <strong>${escapeHtml(t(key, { n }))}</strong>
      ${badge ? `<em>${escapeHtml(badge)}</em>` : ""}
    </div>`;
}

// ------------------------------------------------------------------ bandeja

/** El encargo del proximo partido, arriba del todo: es lo que hay que hacer. */
function missionHtml(c: Career): string {
  const f = nextFixture(c);
  const o = c.objective;
  if (!f || !o || o.season !== f.season || o.matchday !== f.matchday || o.result) return "";
  return `
    <section class="ph-mission">
      <span class="ph-mission-kicker">🎯 ${escapeHtml(t("Encargo del míster"))}</span>
      <strong>${escapeHtml(t(objectiveKey(o), { n: o.target }))}</strong>
      <small>${escapeHtml(t("Contra el {rival} · Jornada {n}", { rival: f.opponent.name, n: f.matchday + 1 }))}</small>
    </section>`;
}

export function phoneInboxHtml(c: Career, seen: SeenMap): string {
  const rows = threadsOf(c)
    .map(({ id, messages }) => {
      const last = messages.at(-1)!;
      const title = threadTitle(c, id);
      const unread = unreadIn(messages, seen[id] ?? 0);
      const waiting = messages.some((m) => canAnswer(c, m));
      const preview = `${last.mine ? `${t("Tú")}: ` : last.who ? `${last.who.split(" ")[0]}: ` : ""}${phoneText(last)}`;
      return `
        <li>
          <button type="button" class="ph-thread${unread ? " is-unread" : ""}" data-act="thread" data-thread="${id}">
            <span class="ph-avatar is-${AVATAR[id].tone}" aria-hidden="true">${AVATAR[id].icon}</span>
            <span class="ph-thread-body">
              <span class="ph-thread-top"><strong>${escapeHtml(title.name)}</strong><small>${escapeHtml(whenText(last))}</small></span>
              <span class="ph-thread-preview">${escapeHtml(preview)}</span>
            </span>
            ${waiting ? `<span class="ph-pill">${escapeHtml(t("Responder"))}</span>` : ""}
            ${unread ? `<i class="ph-unread">${unread > 9 ? "9+" : unread}</i>` : ""}
          </button>
        </li>`;
    })
    .join("");
  return `
    <div class="ph">
      ${relationsHtml(careerRelations(c))}
      ${missionHtml(c)}
      <ul class="ph-threads">${rows}</ul>
    </div>`;
}

// ------------------------------------------------------------------ conversacion

function likesOf(m: PhoneMessage): number {
  // Likes de mentira pero estables: el mismo post, los mismos siempre.
  return ((m.id * 2654435761) >>> 0) % 480 + 12;
}

function bubbleHtml(c: Career, m: PhoneMessage, thread: ThreadId, isNew: boolean, i: number): string {
  const text = escapeHtml(phoneText(m));
  const cls = `ph-bubble${m.mine ? " is-mine" : ""}${isNew ? " is-new" : ""}`;
  const style = isNew ? ` style="--i:${i}"` : "";
  if (thread === "press" && !m.mine) {
    return `<li class="${cls} is-news"${style}><span class="ph-news-kicker">${escapeHtml(PAPER_NAME)} · ${escapeHtml(whenText(m))}</span><p>${text}</p></li>`;
  }
  if (thread === "fans" && !m.mine) {
    const handle = typeof m.params?.who === "string" ? m.params.who : "@grada";
    return `<li class="${cls} is-post"${style}><span class="ph-post-handle">${escapeHtml(handle)}</span><p>${text}</p><span class="ph-post-likes">❤ ${likesOf(m)}</span></li>`;
  }
  const who = !m.mine && m.who ? `<span class="ph-who">${escapeHtml(m.who)}</span>` : "";
  const card = m.card === "scout" ? scoutCardHtml(c, m) : m.card === "objective" ? objectiveCardHtml(c, m) : "";
  return `<li class="${cls}"${style}>${who}<p>${text}</p>${card}</li>`;
}

export function phoneChatHtml(c: Career, thread: ThreadId, ui: PhoneUi, seenBefore: number): string {
  const messages = c.feed.filter((m) => m.from === thread);
  const title = threadTitle(c, thread);
  let lastDay = "";
  let fresh = 0;
  const items: string[] = [];
  for (const m of messages) {
    if (ui.typing !== null && m.id >= ui.typing) continue;
    const day = whenText(m);
    if (day !== lastDay) {
      items.push(`<li class="ph-day">${escapeHtml(day)}</li>`);
      lastDay = day;
    }
    const isNew = m.id > seenBefore && !m.mine;
    items.push(bubbleHtml(c, m, thread, isNew, isNew ? fresh++ : 0));
  }
  if (ui.effect) items.push(effectHtml(ui.effect));
  if (ui.typing !== null) {
    items.push(`<li class="ph-typing" aria-label="${escapeHtml(t("Escribiendo…"))}"><i></i><i></i><i></i></li>`);
  }
  // La pregunta que aun se puede contestar (la ultima de esta conversacion).
  const open = ui.typing === null ? [...messages].reverse().find((m) => canAnswer(c, m)) : undefined;
  const question = open ? questionOf(open.ask) : null;
  const replies =
    open && question
      ? `<div class="ph-replies" role="group" aria-label="${escapeHtml(t("Responder"))}">
          ${question.options
            .map(
              (o, i) =>
                `<button type="button" class="ph-reply" data-act="reply" data-msg="${open.id}" data-opt="${i}">${escapeHtml(t(o.say))}</button>`,
            )
            .join("")}
        </div>`
      : "";
  return `
    <div class="ph ph--chat">
      <header class="ph-chat-head">
        <button type="button" class="ph-back" data-act="inbox" aria-label="${escapeHtml(t("Volver a los chats"))}">‹</button>
        <span class="ph-avatar is-${AVATAR[thread].tone}" aria-hidden="true">${AVATAR[thread].icon}</span>
        <span class="ph-chat-who"><strong>${escapeHtml(title.name)}</strong><small>${escapeHtml(title.sub)}</small></span>
      </header>
      <ol class="ph-chat" data-chat>${items.join("")}</ol>
      ${replies}
    </div>`;
}
